import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { ReplyToMessage } from "entities/domain/conversations/message-domain";
import { RootState } from "redux/store";
import {
  ALL_INBOXES_SCOPE,
  InboxScope,
  LegacyTeamScope,
  deserializeInboxScope,
  deserializeLegacyTeamScope,
  serializeInboxScope,
} from "util/inbox-scope";

export const TEXT_LIMITS_PER_CHANNEL = {
  whatsapp: 1600,
};

export interface AssignPayload {
  agentId?: number | null;
  aiAssistantId?: string | null;
  conversationId: number;
}

export enum ConversationTab {
  Personal = "personal",
  Team = "team",
  Unassigned = "unassigned",
}

export enum OpenClosedFilter {
  Open = "open",
  Closed = "closed",
}

export enum SearchField {
  Name = "name",
  Phone = "phone",
  KeyloopRef = "keyloop_ref",
  Plate = "plate",
  Vin = "vin",
}

export enum ConversationSortOrder {
  UnreadFirst = "unread_first",
  LastMessageAt = "last_message_at",
}

interface CreateConversationState {
  isVisible: boolean;
  loading: boolean;
  errors: string[];
}

export interface MessageInputAttachment {
  id: string;
  url: string;
  type: string;
  name?: string;
}

export enum MessageInputTab {
  EDIT = 0,
  PREVIEW = 1,
}

export interface MessageInputState {
  text: string;
  attachments: MessageInputAttachment[];
  templateId: string | null; // Store template ID instead of instance
  templateName: string | null; // Store template name for display
  customFields: Record<string, string>; // Store custom fields separately
  subject: string;
  activeTab: MessageInputTab;
  isPreparingInput: boolean;
  replyToMessage?: { conversationId: number; preview: ReplyToMessage };
  replySuggestionId?: string;
  replySuggestionCustomFields?: Record<string, string>;
}

export const isMessageInputState = (state: any): state is MessageInputState => {
  return (
    state &&
    typeof state.text === "string" &&
    Array.isArray(state.attachments) &&
    (state.templateId === null || typeof state.templateId === "string") &&
    (state.subject === null || typeof state.subject === "string") &&
    (state.activeTab === MessageInputTab.EDIT ||
      state.activeTab === MessageInputTab.PREVIEW)
  );
};

export const isMessageInputStateEmpty = (state: MessageInputState) => {
  return (
    state.text === "" &&
    state.attachments.length === 0 &&
    state.templateId === null &&
    !state.subject
  );
};

export const getMessageInputStateFromLocalStorage = (
  id: string
): MessageInputState | null => {
  if (!id) {
    return null;
  }

  try {
    const draftState = localStorage.getItem(id);

    if (draftState) {
      const messageInputState = JSON.parse(draftState);

      if (!isMessageInputState(messageInputState)) {
        localStorage.removeItem(id);

        return null;
      }

      return messageInputState;
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't restore draft state", error);
  }

  return null;
};

export const CONVERSATION_SORT_ORDER_STORAGE_KEY =
  "fuzey:conversations:sortOrder";

export const getSortOrderFromLocalStorage = (): ConversationSortOrder => {
  try {
    const stored = localStorage.getItem(CONVERSATION_SORT_ORDER_STORAGE_KEY);

    if (
      stored &&
      Object.values(ConversationSortOrder).includes(
        stored as ConversationSortOrder
      )
    ) {
      return stored as ConversationSortOrder;
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't restore conversation sort order", error);
  }

  return ConversationSortOrder.LastMessageAt;
};

export const CONVERSATION_TAB_STORAGE_KEY = "fuzey:conversations:activeTab";

// Persisted so the eager conversations prefetch (usePrefetchConversations)
// can guess the tab at auth-ready time instead of waiting for the merchant +
// agents queries to derive the default — that wait put two extra API
// round-trips in front of the inbox LCP.
export const getActiveTabFromLocalStorage = (): ConversationTab | undefined => {
  try {
    const stored = localStorage.getItem(CONVERSATION_TAB_STORAGE_KEY);

    if (
      stored &&
      Object.values(ConversationTab).includes(stored as ConversationTab)
    ) {
      return stored as ConversationTab;
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't restore conversation tab", error);
  }

  return undefined;
};

export const persistActiveTab = (tab: ConversationTab): void => {
  try {
    localStorage.setItem(CONVERSATION_TAB_STORAGE_KEY, tab);
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't persist conversation tab", error);
  }
};

export const INBOX_SCOPE_STORAGE_KEY = "fuzey:conversations:inboxScope";

const INBOX_SCOPE_STORAGE_VERSION = 1;

// Release 1 saved a Team scope here. Read only to carry that choice over to its
// Inbox once the Inbox list is known, then removed.
export const LEGACY_TEAM_SCOPE_STORAGE_KEY = "fuzey:conversations:teamScope";

const LEGACY_TEAM_SCOPE_STORAGE_VERSION = 1;

interface StoredScope {
  v: number;
  scope: string;
}

const isStoredScope = (value: unknown): value is StoredScope =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as StoredScope).v === "number" &&
  typeof (value as StoredScope).scope === "string";

const readStoredScope = (key: string, version: number): string | undefined => {
  const stored = localStorage.getItem(key);

  if (!stored) {
    return undefined;
  }

  const parsed: unknown = JSON.parse(stored);

  if (!isStoredScope(parsed) || parsed.v !== version) {
    return undefined;
  }

  return parsed.scope;
};

/**
 * The Inbox scope the actor last chose on this device. Versioned so that a
 * future shape change discards old entries instead of half-reading them; any
 * parse failure discards too, and the inbox opens on All.
 *
 * Per-device on purpose (plan decision D7): there is no server-side preference.
 */
export const getInboxScopeFromLocalStorage = (): InboxScope | undefined => {
  try {
    return (
      deserializeInboxScope(
        readStoredScope(INBOX_SCOPE_STORAGE_KEY, INBOX_SCOPE_STORAGE_VERSION)
      ) ?? undefined
    );
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't restore conversation inbox scope", error);
  }

  return undefined;
};

/** A Release 1 Team choice still waiting to be carried over to its Inbox. */
export const getLegacyTeamScopeFromLocalStorage = ():
  | LegacyTeamScope
  | undefined => {
  try {
    return (
      deserializeLegacyTeamScope(
        readStoredScope(
          LEGACY_TEAM_SCOPE_STORAGE_KEY,
          LEGACY_TEAM_SCOPE_STORAGE_VERSION
        )
      ) ?? undefined
    );
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't restore conversation team scope", error);
  }

  return undefined;
};

export const clearLegacyTeamScopeFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(LEGACY_TEAM_SCOPE_STORAGE_KEY);
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't clear conversation team scope", error);
  }
};

export const persistInboxScope = (scope: InboxScope): void => {
  try {
    localStorage.setItem(
      INBOX_SCOPE_STORAGE_KEY,
      JSON.stringify({
        v: INBOX_SCOPE_STORAGE_VERSION,
        scope: serializeInboxScope(scope),
      })
    );
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't persist conversation inbox scope", error);
  }
};

export const persistSortOrder = (sortOrder: ConversationSortOrder): void => {
  try {
    localStorage.setItem(CONVERSATION_SORT_ORDER_STORAGE_KEY, sortOrder);
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error("Couldn't persist conversation sort order", error);
  }
};

export interface ActiveCall {
  isActive: boolean;
  isAiCall: boolean;
  voiceScriptId?: string;
}

interface ConversationsState {
  selectedConversationIds: number[];
  activeConversationId: number | undefined;
  isSummaryPanelCollapsed: boolean;
  isContactDetailsPanelCollapsed: boolean;
  searchText: string;
  searchField: SearchField;
  activeTab: ConversationTab | undefined;
  new: CreateConversationState;
  isOpenOrClosed: OpenClosedFilter;
  queuedCloseConversationId: number | null;
  messageInput: MessageInputState;
  filterChannels: string[];
  filterAgents: string[];
  filterCustomerTagIds: string[];
  filterSentiment: string | null;
  filterUrgency: string | null;
  sortOrder: ConversationSortOrder;
  bulkActionsToolbarEnabled: boolean;
  selectedInbox: InboxScope;
  bulkActionsToolbarLoading: boolean;
  activeConversationAiBoundary: string | null;
  activeCalls: Record<number, ActiveCall>;
}

const initialState: ConversationsState = {
  new: {
    isVisible: false,
    loading: false,
    errors: [],
  },
  messageInput: {
    text: "",
    attachments: [],
    subject: "",
    templateId: null,
    templateName: null,
    customFields: {},
    activeTab: MessageInputTab.EDIT,
    isPreparingInput: false,
  },
  selectedConversationIds: [],
  searchText: "",
  searchField: SearchField.Name,
  selectedInbox: ALL_INBOXES_SCOPE,
  activeConversationId: undefined,
  isSummaryPanelCollapsed: true,
  isContactDetailsPanelCollapsed: false,
  activeTab: undefined,
  isOpenOrClosed: OpenClosedFilter.Open,
  queuedCloseConversationId: null,
  filterChannels: [],
  filterAgents: [],
  filterCustomerTagIds: [],
  filterSentiment: null,
  filterUrgency: null,
  sortOrder: getSortOrderFromLocalStorage(),
  bulkActionsToolbarEnabled: true,
  bulkActionsToolbarLoading: false,
  activeConversationAiBoundary: null,
  activeCalls: {},
};

// Removed getRefreshedConversations, sortTemplatesAlphabetically, appendOrUpdateTemplate
// These are no longer needed as backend data is in TanStack Query cache

const sameMembers = (
  current: string[] | undefined,
  next: string[]
): boolean => {
  const existing = current ?? [];
  if (existing.length !== next.length) {
    return false;
  }
  for (let index = 0; index < existing.length; index += 1) {
    if (existing[index] !== next[index]) {
      return false;
    }
  }
  return true;
};

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    // Removed fetchConversations, fetchConversationsSuccess, fetchConversationsFail
    // Loading state is now handled by TanStack Query
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload.trim().replace(/\s{2,}/g, " ");
    },
    setSearchField: (state, action: PayloadAction<SearchField>) => {
      state.searchField = action.payload;
    },
    clearMessageInput: (state) => {
      state.messageInput.text = "";
      state.messageInput.attachments = [];
      state.messageInput.templateId = null;
      state.messageInput.templateName = null;
      state.messageInput.customFields = {};
      state.messageInput.subject = "";
      state.messageInput.activeTab = MessageInputTab.EDIT;
      state.messageInput.replyToMessage = undefined;
      state.messageInput.replySuggestionId = undefined;
      state.messageInput.replySuggestionCustomFields = undefined;
    },
    setMessageInputReplyToMessage: (
      state,
      action: PayloadAction<MessageInputState["replyToMessage"]>
    ) => {
      state.messageInput.replyToMessage = action.payload;
    },
    setMessageInputState: (state, action: PayloadAction<MessageInputState>) => {
      state.messageInput = action.payload;
    },
    setMessageInputReplySuggestionCustomFields: (
      state,
      action: PayloadAction<Record<string, string> | undefined>
    ) => {
      state.messageInput.replySuggestionCustomFields = action.payload;
    },
    setMessageInputIsPreparing: (state, action: PayloadAction<boolean>) => {
      state.messageInput.isPreparingInput = action.payload;
    },
    setMessageInputTemplate: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        customFields: Record<string, string>;
      } | null>
    ) => {
      if (action.payload) {
        state.messageInput.replyToMessage = undefined;
        state.messageInput.templateId = action.payload.id;
        state.messageInput.templateName = action.payload.name;
        state.messageInput.customFields = action.payload.customFields;
      } else {
        state.messageInput.templateId = null;
        state.messageInput.templateName = null;
        state.messageInput.customFields = {};
      }
    },
    clearAttahmentsFromMessageInput: (state) => {
      state.messageInput.attachments = [];
    },
    changeMessageInputText: (state, action: PayloadAction<string>) => {
      state.messageInput.text = action.payload;
    },
    changeMessageInputSubject: (state, action: PayloadAction<string>) => {
      state.messageInput.subject = action.payload;
    },
    updateMessageInputActiveTab: (
      state,
      action: PayloadAction<MessageInputTab>
    ) => {
      state.messageInput.activeTab = action.payload;
    },
    addAttachmentToMessageInput: (
      state,
      action: PayloadAction<MessageInputAttachment>
    ) => {
      state.messageInput.attachments = [action.payload]; // Because we don't allow more than 1 at the moment
    },
    removeAttachmentFromMessageInput: (
      state,
      action: PayloadAction<number>
    ) => {
      state.messageInput.attachments = state.messageInput.attachments.filter(
        (_, index) => index !== action.payload
      );
    },
    setActiveConversation: (
      state,
      action: PayloadAction<{
        conversationId: number | undefined;
        isPreloaded?: boolean;
      }>
    ) => {
      if (state.activeConversationId !== action.payload.conversationId) {
        state.messageInput.replyToMessage = undefined;
      }
      state.activeConversationId = action.payload.conversationId;
    },
    toggleSummaryPanel: (state) => {
      state.isSummaryPanelCollapsed = !state.isSummaryPanelCollapsed;
    },
    setContactDetailsPanelCollapsed: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isContactDetailsPanelCollapsed = action.payload;
    },
    setSelectedInbox: (state, action: PayloadAction<InboxScope>) => {
      state.selectedInbox = action.payload;
    },
    // Removed backend data reducers: setConversations, bulkAppendOrReplaceConversations,
    // appendOrReplaceConversation, updateConversationIsSubscribed, updateConversationIsOpen,
    // updateConversationTagIds, updateConversationIsRead, updateConversationAgentAndTeamIds,
    // clearTemplates, setTemplates, propagateTemplateUpdate
    // These are now handled by TanStack Query cache updates
    setActiveConversationTab: (
      state,
      action: PayloadAction<ConversationTab | undefined>
    ) => {
      state.activeTab = action.payload;
    },
    setActiveConversationOpenClosedFilter: (
      state,
      action: PayloadAction<OpenClosedFilter>
    ) => {
      state.isOpenOrClosed = action.payload;
    },
    // Close requested while a message send was still in flight. The close is applied
    // after the send settles (SendButton) so the backend never processes them out of
    // order — a send landing after a close reopens the conversation server-side.
    setQueuedCloseConversationId: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.queuedCloseConversationId = action.payload;
    },
    // The filter arrays are effect dependencies (DesktopTabsArea clears the bulk
    // selection when a filter changes). Applying the filter dialog re-dispatches
    // every filter, so keep the stored array when the members are unchanged:
    // a sort-only change must not read as a filter change.
    setFilterChannels: (state, action: PayloadAction<string[]>) => {
      if (!sameMembers(state.filterChannels, action.payload)) {
        state.filterChannels = action.payload;
      }
    },
    setFilterAgents: (state, action: PayloadAction<string[]>) => {
      if (!sameMembers(state.filterAgents, action.payload)) {
        state.filterAgents = action.payload;
      }
    },
    setFilterCustomerTagIds: (state, action: PayloadAction<string[]>) => {
      if (!sameMembers(state.filterCustomerTagIds, action.payload)) {
        state.filterCustomerTagIds = action.payload;
      }
    },
    setFilterSentiment: (state, action: PayloadAction<string | null>) => {
      state.filterSentiment = action.payload;
    },
    setFilterUrgency: (state, action: PayloadAction<string | null>) => {
      state.filterUrgency = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<ConversationSortOrder>) => {
      state.sortOrder = action.payload;
    },
    updateConversationSelection: (
      state,
      action: PayloadAction<{ conversationId: number; isSelected: boolean }>
    ) => {
      if (
        action.payload.isSelected &&
        !state.selectedConversationIds.includes(action.payload.conversationId)
      ) {
        state.selectedConversationIds.push(action.payload.conversationId);
      } else if (!action.payload.isSelected) {
        state.selectedConversationIds = state.selectedConversationIds.filter(
          (id) => id !== action.payload.conversationId
        );
      }
    },
    clearSelectedConversations: (state) => {
      state.selectedConversationIds = [];
    },
    // Removed backend reducers: markConversationsAsRead/Unread,
    // markConversationsAsOpenedOrClosed, assignAgentToConversations
    enableBulkActionsToolbar: (state) => {
      state.bulkActionsToolbarEnabled = true;
    },
    disableBulkActionsToolbar: (state) => {
      state.bulkActionsToolbarEnabled = false;
    },
    startLoadingBulkActionsToolbar: (state) => {
      state.bulkActionsToolbarLoading = true;
    },
    stopLoadingBulkActionsToolbar: (state) => {
      state.bulkActionsToolbarLoading = false;
    },
    setActiveConversationAiBoundary: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.activeConversationAiBoundary = action.payload;
    },
    resetStore: (state) => {
      state.new = {
        isVisible: false,
        loading: false,
        errors: [],
      };
      state.selectedConversationIds = [];
      // state.searchText = ""; // Do not reset when merchant changes
      state.selectedInbox = ALL_INBOXES_SCOPE;
      state.activeConversationId = undefined;
      state.isSummaryPanelCollapsed = true;
      state.isContactDetailsPanelCollapsed = false;
      state.activeTab = undefined;
      state.isOpenOrClosed = OpenClosedFilter.Open;
      state.filterChannels = [];
      state.filterAgents = [];
      state.filterCustomerTagIds = [];
      state.filterSentiment = null;
      state.filterUrgency = null;
      // Sort order is a persisted UI preference, so keep it aligned with
      // localStorage rather than discarding it when the store resets.
      state.sortOrder = getSortOrderFromLocalStorage();
      state.bulkActionsToolbarEnabled = true;
      state.bulkActionsToolbarLoading = false;
      state.activeConversationAiBoundary = null;
      state.activeCalls = {};
      state.messageInput = {
        text: "",
        attachments: [],
        subject: "",
        templateId: null,
        templateName: null,
        customFields: {},
        activeTab: MessageInputTab.EDIT,
        isPreparingInput: false,
        replySuggestionId: undefined,
        replySuggestionCustomFields: undefined,
      };
    },
    // Removed backend reducer: updateConversationIsBlocked
    setCallActive: (
      state,
      action: PayloadAction<{
        conversationId: number;
        isAiCall: boolean;
        voiceScriptId?: string;
      }>
    ) => {
      state.activeCalls[action.payload.conversationId] = {
        isActive: true,
        isAiCall: action.payload.isAiCall,
        voiceScriptId: action.payload.voiceScriptId,
      };
    },
    stopCall: (state, action: PayloadAction<number>) => {
      delete state.activeCalls[action.payload];
    },
    updateCallStatus: (
      state,
      action: PayloadAction<{
        conversationId: number;
        isActive: boolean;
      }>
    ) => {
      if (state.activeCalls[action.payload.conversationId]) {
        state.activeCalls[action.payload.conversationId].isActive =
          action.payload.isActive;
      }
    },
  },
});

export const activeConversationIdSelector = (state: RootState) =>
  state.conversations.activeConversationId;

export const searchTextSelector = (state: RootState) =>
  state.conversations.searchText;

export const searchFieldSelector = (state: RootState) =>
  state.conversations.searchField;

export const activeTabSelector = (state: RootState) =>
  state.conversations.activeTab;

export const openClosedSelector = (state: RootState) =>
  state.conversations.isOpenOrClosed;

export const filterChannelsSelector = (state: RootState) =>
  state.conversations.filterChannels;

export const filterAgentsSelector = (state: RootState) =>
  state.conversations.filterAgents;

export const filterCustomerTagIdsSelector = (state: RootState) =>
  state.conversations.filterCustomerTagIds;

export const filterSentimentSelector = (state: RootState) =>
  state.conversations.filterSentiment;

export const filterUrgencySelector = (state: RootState) =>
  state.conversations.filterUrgency;

export const sortOrderSelector = (state: RootState) =>
  state.conversations.sortOrder;

export const activeConversationAiBoundarySelector = (state: RootState) =>
  state.conversations.activeConversationAiBoundary;

export const activeCallSelector =
  (conversationId: number) => (state: RootState) =>
    state.conversations.activeCalls[conversationId] || null;

// Keep backward compatibility alias
export const activeAiCallSelector = activeCallSelector;

export const selectedConversationIdsSelector = (state: RootState) =>
  state.conversations.selectedConversationIds;

export const isSummaryPanelCollapsedSelector = (state: RootState) =>
  state.conversations.isSummaryPanelCollapsed;

export const isContactDetailsPanelCollapsedSelector = (state: RootState) =>
  state.conversations.isContactDetailsPanelCollapsed;

export const selectedInboxSelector = (state: RootState) =>
  state.conversations.selectedInbox;

export const {
  setActiveConversation,
  toggleSummaryPanel,
  setContactDetailsPanelCollapsed,
  setActiveConversationTab,
  setActiveConversationOpenClosedFilter,
  setQueuedCloseConversationId,
  setFilterChannels,
  updateMessageInputActiveTab,
  setFilterAgents,
  setFilterCustomerTagIds,
  setFilterSentiment,
  setFilterUrgency,
  setSortOrder,
  setSearchText,
  setSearchField,
  updateConversationSelection,
  setMessageInputReplySuggestionCustomFields,
  clearSelectedConversations,
  enableBulkActionsToolbar,
  disableBulkActionsToolbar,
  addAttachmentToMessageInput,
  setMessageInputIsPreparing,
  changeMessageInputText,
  setMessageInputState,
  changeMessageInputSubject,
  clearMessageInput,
  setMessageInputReplyToMessage,
  startLoadingBulkActionsToolbar,
  stopLoadingBulkActionsToolbar,
  setMessageInputTemplate,
  setSelectedInbox,
  removeAttachmentFromMessageInput,
  clearAttahmentsFromMessageInput,
  resetStore,
  setActiveConversationAiBoundary,
  updateCallStatus,
  setCallActive,
  stopCall,
} = conversationsSlice.actions;
export const conversationsSliceActions = conversationsSlice.actions;
export default conversationsSlice.reducer;
